import {
  AutopilotTaskDto,
  TaskCategoryStage,
} from '@urbanx/agx.activities.client'
import { BadgeColor } from '@urbanx/agx.ui-components2'
import { splitPascalCase } from 'utils/stringUtils'

export enum WorkflowStageIndicatorStatus {
  COMPLETED = 'COMPLETED',
  INPROGRESS = 'INPROGRESS',
  ISPENDING = 'ISPENDING',
  LOCKED = 'LOCKED',
}

export interface WorkflowStageIndicator {
  name: string
  color: BadgeColor
  status: WorkflowStageIndicatorStatus
  stage?: TaskCategoryStage
}

const stageOrder = Object.values(TaskCategoryStage)

const campaignStages = stageOrder.slice(
  0,
  stageOrder.indexOf(TaskCategoryStage.Withdrawn) + 1
)
const campaignBeginningStages = [TaskCategoryStage.AgreementCompliance]

const contractStages = stageOrder.slice(
  stageOrder.indexOf(TaskCategoryStage.ContractCompliance),
  stageOrder.indexOf(TaskCategoryStage.Settlement) + 1
)
const contractBeginningStages = [
  TaskCategoryStage.ContractCompliance,
  TaskCategoryStage.ContractRequest,
]

const progressionPalette: BadgeColor[] = ['blue', 'purple', 'orange', 'teal']
const grey: BadgeColor = 'grey'
const lightGrey: BadgeColor = 'lightgrey'

const getWorkflowStageConfig = (
  stage?: TaskCategoryStage
): { beginningStages: TaskCategoryStage[] } => {
  if (stage && contractStages.includes(stage)) {
    return { beginningStages: contractBeginningStages }
  }

  if (stage && campaignStages.includes(stage)) {
    return { beginningStages: campaignBeginningStages }
  }

  return { beginningStages: campaignBeginningStages }
}

const getWorkflowStageColorByOrder = (
  stage: TaskCategoryStage,
  orderedWorkflowStages: TaskCategoryStage[],
  isBeginningStage: boolean
): BadgeColor => {
  const stageIndex = orderedWorkflowStages.indexOf(stage)
  const lastStageIndex = orderedWorkflowStages.length - 1

  if (lastStageIndex < 0 || stageIndex < 0) {
    return progressionPalette[0]
  }

  if (stageIndex === lastStageIndex) {
    return 'green'
  }

  const computedIndex = isBeginningStage ? stageIndex : stageIndex
  return progressionPalette[computedIndex % progressionPalette.length]
}

const getUniqueOrderedWorkflowStages = (
  orderedTasks: Required<AutopilotTaskDto>[]
): TaskCategoryStage[] =>
  Array.from(
    new Set(
      orderedTasks
        .map((task) => task.category?.stage)
        .filter((stage): stage is TaskCategoryStage => Boolean(stage))
    )
  )

export const getWorkflowBadges = (
  tasks: Required<AutopilotTaskDto>[]
): WorkflowStageIndicator[] => {
  const withdrawnTask = tasks.find(
    (task) => task.category?.stage === TaskCategoryStage.Withdrawn
  )
  const terminatedTask = tasks.find(
    (task) => task.category?.stage === TaskCategoryStage.Termination
  )

  const orderedTasks = [...tasks].sort((a, b) => a.order - b.order)

  const taskInProgress = orderedTasks.find((task) => task.inProgress)
  const firstTaskReadyToProcess = orderedTasks.find(
    (task) => !task.isCompleted && !task.isLocked
  )
  const allCompleted =
    orderedTasks.length > 0 && orderedTasks.every((task) => task.isCompleted)

  const currentTask = taskInProgress ?? firstTaskReadyToProcess
  const currentStage = currentTask?.category?.stage
  const { beginningStages } = getWorkflowStageConfig(
    currentStage ?? orderedTasks[0]?.category.stage
  )
  const orderedWorkflowStages = getUniqueOrderedWorkflowStages(orderedTasks)
  const currentStageIndex = currentStage
    ? orderedWorkflowStages.indexOf(currentStage)
    : allCompleted
      ? orderedWorkflowStages.length
      : -1

  const getNonInProgressWorkflowBadge = (
    name: string,
    stage: TaskCategoryStage,
    status: WorkflowStageIndicatorStatus
  ): WorkflowStageIndicator => ({
    name,
    stage,
    color: lightGrey,
    status,
  })

  const getWorkflowStageLabel = (type?: TaskCategoryStage) =>
    splitPascalCase(type)

  const isWithdrawnOrTerminated = Boolean(withdrawnTask || terminatedTask)
  const withdrawnOrTerminatedCompleted =
    withdrawnTask?.isCompleted || terminatedTask?.isCompleted
  const isWithdrawalOrTerminationStage = (taskStage: TaskCategoryStage) =>
    taskStage === TaskCategoryStage.Withdrawn ||
    taskStage === TaskCategoryStage.Termination

  return [
    ...orderedWorkflowStages.map((taskStage, index) => {
      if (
        isWithdrawnOrTerminated &&
        isWithdrawalOrTerminationStage(taskStage)
      ) {
        return {
          status: withdrawnOrTerminatedCompleted
            ? WorkflowStageIndicatorStatus.COMPLETED
            : WorkflowStageIndicatorStatus.INPROGRESS,
          stage: taskStage,
          name: getWorkflowStageLabel(taskStage),
          color: (
            withdrawnOrTerminatedCompleted
              ? lightGrey
              : getWorkflowStageColorByOrder(
                  taskStage,
                  orderedWorkflowStages,
                  beginningStages.includes(taskStage)
                )
          ) as BadgeColor,
        }
      }

      if (currentTask?.category?.stage === taskStage && !isWithdrawnOrTerminated) {
        return {
          status: WorkflowStageIndicatorStatus.INPROGRESS,
          stage: taskStage,
          name: getWorkflowStageLabel(taskStage),
          color: getWorkflowStageColorByOrder(
            taskStage,
            orderedWorkflowStages,
            beginningStages.includes(taskStage)
          ),
        }
      }

      const status = isWithdrawnOrTerminated
        ? WorkflowStageIndicatorStatus.COMPLETED
        : currentStageIndex === -1
          ? WorkflowStageIndicatorStatus.LOCKED
          : index < currentStageIndex
            ? WorkflowStageIndicatorStatus.COMPLETED
            : WorkflowStageIndicatorStatus.LOCKED

      return getNonInProgressWorkflowBadge(
        getWorkflowStageLabel(taskStage),
        taskStage,
        status
      )
    }),
    {
      name: 'Complete',
      color: allCompleted ? grey : lightGrey,
      status: WorkflowStageIndicatorStatus.LOCKED,
    },
  ]
}

export const findInProgressWorkflowStage = (
  workflowBadges: WorkflowStageIndicator[]
) =>
  workflowBadges.find(
    (badge) => badge.status === WorkflowStageIndicatorStatus.INPROGRESS
  )?.stage

export const findFirstWorkflowStage = (
  workflowBadges: WorkflowStageIndicator[]
) => workflowBadges.find((badge) => badge.stage)?.stage

export const getDefaultSelectedWorkflowStage = (
  workflowBadges: WorkflowStageIndicator[]
) => findInProgressWorkflowStage(workflowBadges) ?? findFirstWorkflowStage(workflowBadges)

export const getNextInProgressWorkflowStage = (
  workflowBadges: WorkflowStageIndicator[]
) =>
  workflowBadges.find(
    (badge) =>
      badge.status === WorkflowStageIndicatorStatus.INPROGRESS && badge.stage
  )?.stage
